/**
 * Spike strip (`course.spike_strip.v1`) — a tileable jump-course hazard: a riveted iron rail carrying
 * a row of iron teeth. `facing` 'up' sits on a floor (rail at the bottom, teeth up); 'down' hangs
 * under a wall or ceiling (rail at the top, teeth down). Frame 70×16; strips placed side by side make
 * any width. `teeth` (integer 4..7) per strip, `rust` (0..1) warm corrosion. The art is only the
 * warning: the kill area is a `spikes` zone authored in the level, slightly smaller than the art.
 * Deterministic for the recipe; the seed varies tooth heights and rust flecks.
 */
import { checkEnvelope, checkScope, el, mix, n, partGroup, poly } from '../../svg';
import { rngFor } from '../../rng';
import type { PieceArt, PieceRecipe, ResolvedPalette } from '../../contract';

export type SpikeFacing = 'up' | 'down';
export interface SpikeStripParams {
  readonly facing: SpikeFacing;
  readonly teeth: number; // integer 4..7
  readonly rust: number;  // 0..1
}
export type SpikeStripRecipe = PieceRecipe<'course.spike_strip.v1', SpikeStripParams>;

export const SPIKE_STRIP_FRAME = { w: 70, h: 16 } as const;

export const COURSE_SPIKE_STRIP: SpikeStripRecipe = {
  schema: 'hush.asset-recipe.v1', id: 'course.spike_strip.floor.v1', generatorId: 'course.spike_strip.v1',
  seed: 20071201, paletteId: 'nook_warm', params: { facing: 'up', teeth: 6, rust: 0.4 },
};
export const COURSE_SPIKE_STRIP_HANGING: SpikeStripRecipe = {
  ...COURSE_SPIKE_STRIP, id: 'course.spike_strip.hanging.v1', seed: 20071202, params: { facing: 'down', teeth: 6, rust: 0.4 },
};

function amount(p: unknown, key: string, lo: number, hi: number, integer = false): number {
  const v = (p as Record<string, unknown>)[key];
  if (typeof v !== 'number' || !(v >= lo && v <= hi) || (integer && !Number.isInteger(v))) {
    throw new Error(`course.spike_strip.v1: ${key} must be ${integer ? 'an integer ' : ''}${lo}..${hi}`);
  }
  return v;
}

export function buildSpikeStrip(recipe: SpikeStripRecipe, p: ResolvedPalette, scope: string): PieceArt {
  checkEnvelope(recipe, COURSE_SPIKE_STRIP.generatorId); checkScope(scope);
  const { facing } = recipe.params;
  if (facing !== 'up' && facing !== 'down') throw new Error('course.spike_strip.v1: facing must be up or down');
  const teeth = amount(recipe.params, 'teeth', 4, 7, true), rust = amount(recipe.params, 'rust', 0, 1);
  const random = rngFor(recipe), parts: string[] = [];
  const { w: W, h: H } = SPIKE_STRIP_FRAME;
  const up = facing === 'up', railH = 4;
  // Draw as if facing up (rail at the bottom), then mirror vertically for 'down'.
  const iron = mix(p.outline, p.stoneShadow, 0.45), ironLight = mix(p.stoneLight, p.salt, 0.3);
  const rustTone = mix(p.woodShadow, p.paperShadow, 0.35);
  let rail = el('rect', { x: 0.5, y: H - railH, width: W - 1, height: railH - 0.5, fill: mix(p.woodShadow, p.outline, 0.3), stroke: p.outline, 'stroke-width': 0.8 });
  for (let i = 0; i < 3; i++) rail += el('circle', { cx: 8 + i * (W - 16) / 2, cy: H - railH / 2, r: 0.9, fill: ironLight });
  let row = '';
  const step = (W - 2) / teeth;
  for (let i = 0; i < teeth; i++) {
    const left = 1 + i * step, mid = left + step / 2, tip = 1 + random(`tooth.${i}`) * 2.5;
    row += el('path', { d: poly([{ x: left + 0.4, y: H - railH }, { x: mid, y: tip }, { x: left + step - 0.4, y: H - railH }]), fill: iron, stroke: p.outline, 'stroke-width': 0.7 })
      + el('path', { d: `M${n(mid)} ${n(tip + 1.5)}L${n(mid + step * 0.22)} ${n(H - railH - 0.5)}`, fill: 'none', stroke: ironLight, 'stroke-width': 0.7, opacity: 0.8 });
    if (random(`rust.${i}`) < rust) {
      row += el('path', { d: poly([{ x: left + step * 0.2, y: H - railH }, { x: mid - 0.6, y: H - railH - 4 - random(`rh.${i}`) * 3 }, { x: left + step * 0.75, y: H - railH }]), fill: rustTone, opacity: 0.75 });
    }
  }
  const flip = up ? undefined : `matrix(1 0 0 -1 0 ${H})`;
  const body = el('g', { transform: flip }, partGroup(parts, 'spike-rail', rail) + partGroup(parts, 'spike-teeth', row));
  return {
    frame: SPIKE_STRIP_FRAME, body, parts, openings: [],
    // A floor strip stands on its rail (bottom-center); a hanging strip hangs from its rail (top-center).
    anchors: up ? { support: { x: W / 2, y: H } } : { hang: { x: W / 2, y: 0 } },
  };
}
